import { ReactNode } from 'react';
import { Book } from 'pages/dashboard';
import { renderFormattedDate } from 'utils';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage, useIntl } from 'react-intl';
import { ActivityBoardItem, ActivityType, Project, User } from 'library/types';
import { Box, Drawer, Divider, Typography, IconButton, Toolbar, Stack, Chip } from '@mui/material';

type DetailItem = { label: string; value: ReactNode };

type DetailDrawerProps = {
  users: User[];
  books: Book[];
  products: Project[];
  activityTypes: ActivityType[];
  open: boolean;
  close: () => void;
  detailRow: ActivityBoardItem | null;
};

const DetailDrawer = ({ users, books, products, activityTypes, open, close, detailRow }: DetailDrawerProps) => {
  const intl = useIntl();

  const renderRowDetails = (row: ActivityBoardItem | null): DetailItem[] => {
    if (!row) return [];

    return [
      {
        label: intl.formatMessage({ id: 'activityManaging.book', defaultMessage: 'Proje' }),
        value: books.find((b) => b.id === row.bookId)?.companyName ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'activityManaging.product', defaultMessage: 'Ürün' }),
        value: products.find((p) => p.id === row.projectId)?.title ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'activityManaging.assignedUser', defaultMessage: 'Atanan Kullanıcı' }),
        value: users.find((u) => u.id === row.assignedUserId)?.nameSurname ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'activityManaging.activityType', defaultMessage: 'Aktivite Türü' }),
        value: activityTypes.find((a) => a.id === row.activityTypeId)?.title ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'activityManaging.effortTrackNo', defaultMessage: 'Efor Takip No' }),
        value: row.effortTrackNo ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'activityManaging.description', defaultMessage: 'Açıklama' }),
        value: row.description ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'activityManaging.startDate', defaultMessage: 'Başlangıç Tarihi' }),
        value: row.startDate ? renderFormattedDate(row.startDate, true) : '-',
      },
      {
        label: intl.formatMessage({ id: 'activityManaging.endDate', defaultMessage: 'Bitiş Tarihi' }),
        value: row.endDate ? renderFormattedDate(row.endDate, true) : '-',
      },
      {
        label: intl.formatMessage({ id: 'activityManaging.estimatedHours', defaultMessage: 'Harcanan Süre (saat)' }),
        value: row.estimatedHours ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'activityManaging.isOvertimeWork', defaultMessage: 'Fazla Mesai' }),
        value: row.isOvertimeWork ? (
          <Chip size="small" label={intl.formatMessage({ id: 'common.yes', defaultMessage: 'Evet' })} />
        ) : (
          <Chip size="small" label={intl.formatMessage({ id: 'common.no', defaultMessage: 'Hayır' })} />
        ),
      },
    ];
  };

  return (
    <Drawer anchor="right" open={open} onClose={close}>
      <Toolbar />
      <Box sx={{ width: { xs: '92vw', sm: 520 }, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">
            <FormattedMessage id="common.detail" defaultMessage="Detay" />
          </Typography>
          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
          {detailRow
            ? `${books.find((b) => b.id === detailRow.bookId)?.companyName ?? '-'} / ${products.find((p) => p.id === detailRow.projectId)?.title ?? '-'}`
            : ''}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={1.25}>
          {renderRowDetails(detailRow).map((item) => (
            <Box key={item.label}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Drawer>
  );
};

export default DetailDrawer;
